import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SITE_URL,
  SITE_NAME,
  SITE_OG_IMAGE,
  SITE_LOCALE,
  SITE_LANG,
  SITE_TWITTER,
  absoluteUrl,
} from './seoConfig';

/**
 * Single-source SEO component.
 *
 *   <Seo
 *     title="Sunday School | Ministry of Jesus"
 *     description="..."
 *     path="/services/sunday-school"
 *     keywords={['Sunday school Dublin']}
 *     image="/optional-share.png"
 *     type="website"  // or "article"
 *     noindex={false}
 *     jsonLd={[ schemaObj1, schemaObj2 ]}
 *   />
 *
 * - Adds <title>, description, canonical, OG, Twitter, lang, and any JSON-LD scripts.
 * - Title is auto-suffixed with site name unless `titleExact` is true.
 */
function Seo({
  title,
  description,
  path = '/',
  keywords,
  image,
  type = 'website',
  titleExact = false,
  noindex = false,
  jsonLd = [],
}) {
  const canonical = absoluteUrl(path);
  const fullTitle = titleExact || !title
    ? title || SITE_NAME
    : title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const ogImage = image ? absoluteUrl(image) : SITE_OG_IMAGE;
  const kw = Array.isArray(keywords) ? keywords.join(', ') : keywords;

  return (
    <Helmet prioritizeSeoTags>
      {/* Primary */}
      <html lang={SITE_LANG} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {kw && <meta name="keywords" content={kw} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={`${SITE_NAME} — Pentecostal church in Dublin, Ireland`} />
      <meta property="og:locale" content={SITE_LOCALE} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE_TWITTER} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {/* Local / regional hints */}
      <meta name="geo.region" content="IE-D" />
      <meta name="geo.placename" content="Dublin" />
      <meta name="geo.position" content="53.3425;-6.3608" />
      <meta name="ICBM" content="53.3425, -6.3608" />

      {/* JSON-LD structured data — stable key derived from schema @type to avoid index reconciliation */}
      {jsonLd.map((obj, i) => (
        <script key={`ld-${obj && obj['@type'] ? (Array.isArray(obj['@type']) ? obj['@type'].join('-') : obj['@type']) : 'schema'}-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

export default Seo;
export { absoluteUrl, SITE_URL };
